import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Paper,
  Chip, Switch, FormControlLabel, InputAdornment, Grid, Tooltip, IconButton,
  Divider
} from '@mui/material';
import {
  Edit, Refresh, Search, Notifications as NotifIcon, Sms as SmsIcon,
  WhatsApp as WhatsAppIcon, CheckCircle, EventRepeat, CardGiftcard,
  WavingHand, Receipt, FitnessCenter, Schedule, WarningAmber,
  PersonOff, HelpOutline
} from '@mui/icons-material';
import { marketingAPI, gymsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';

// Action catalog metadata with user-friendly titles, categories, triggers, and supported channels
const ACTION_DEFINITIONS = {
  // ── Membership & Lifecycle (Marketing & Automated) ──
  renewal_reminder: {
    type: 'marketing',
    title: 'Membership Renewal Reminder',
    category: 'Membership & Lifecycle',
    icon: <EventRepeat sx={{ color: '#2563EB' }} />,
    trigger: 'Sent automatically before membership expires based on configured days',
    channels: ['Push', 'SMS', 'WhatsApp'],
    placeholders: ['{{memberName}}', '{{gymName}}', '{{expiryDate}}', '{{daysLeft}}'],
  },
  welcome: {
    type: 'marketing',
    title: 'Welcome New Member',
    category: 'Membership & Lifecycle',
    icon: <WavingHand sx={{ color: '#16A34A' }} />,
    trigger: 'Sent to new members upon registration or onboarding',
    channels: ['Push', 'SMS', 'WhatsApp'],
    placeholders: ['{{memberName}}', '{{gymName}}'],
  },
  birthday: {
    type: 'marketing',
    title: 'Birthday Greetings',
    category: 'Membership & Lifecycle',
    icon: <CardGiftcard sx={{ color: '#D97706' }} />,
    trigger: 'Sent automatically on member’s birthday morning (9:00 AM)',
    channels: ['Push', 'SMS', 'WhatsApp'],
    placeholders: ['{{memberName}}', '{{gymName}}'],
  },

  // ── Invoices & Billing (System Actions) ──
  invoice_created: {
    type: 'gym',
    title: 'Invoice Created / Payment Request',
    category: 'Billing & Invoices',
    icon: <Receipt sx={{ color: '#0284C7' }} />,
    trigger: 'Triggered when a new gym invoice or payment request is issued',
    channels: ['Push'],
    placeholders: ['{{title}}', '{{amount}}', '{{dueDate}}', '{{gymName}}'],
  },
  invoice_due: {
    type: 'gym',
    title: 'Payment Due Reminder',
    category: 'Billing & Invoices',
    icon: <Receipt sx={{ color: '#E11D48' }} />,
    trigger: 'Triggered when an outstanding fee or invoice reaches its due date',
    channels: ['Push'],
    placeholders: ['{{amount}}', '{{gymName}}'],
  },
  invoice_payment_submitted: {
    type: 'gym',
    title: 'Payment Submitted (Verify UTR)',
    category: 'Billing & Invoices',
    icon: <Receipt sx={{ color: '#8B5CF6' }} />,
    trigger: 'Sent to gym staff/admin when member submits a manual payment with UTR',
    channels: ['Push'],
    placeholders: ['{{title}}', '{{amount}}', '{{utrNumber}}'],
  },
  invoice_payment_verified: {
    type: 'gym',
    title: 'Payment Verified & Approved',
    category: 'Billing & Invoices',
    icon: <CheckCircle sx={{ color: '#16A34A' }} />,
    trigger: 'Sent to member when staff confirms and approves their payment',
    channels: ['Push'],
    placeholders: ['{{amount}}', '{{invoiceTitle}}'],
  },
  invoice_payment_rejected: {
    type: 'gym',
    title: 'Payment Rejected',
    category: 'Billing & Invoices',
    icon: <Receipt sx={{ color: '#DC2626' }} />,
    trigger: 'Sent to member if their submitted payment verification is declined',
    channels: ['Push'],
    placeholders: ['{{amount}}', '{{invoiceTitle}}', '{{reason}}'],
  },

  // ── Plans & Training ──
  plan_assigned: {
    type: 'gym',
    title: 'Workout or Diet Plan Assigned',
    category: 'Plans & Training',
    icon: <FitnessCenter sx={{ color: '#4F46E5' }} />,
    trigger: 'Sent when trainer or admin assigns a customized workout/diet plan',
    channels: ['Push'],
    placeholders: ['{{planName}}'],
  },
  plan_updated: {
    type: 'gym',
    title: 'Plan Updated',
    category: 'Plans & Training',
    icon: <FitnessCenter sx={{ color: '#0D9488' }} />,
    trigger: 'Sent when an active routine or meal plan is modified',
    channels: ['Push'],
    placeholders: ['{{planName}}'],
  },
  trainer_query_answered: {
    type: 'gym',
    title: 'Trainer Query Answered',
    category: 'Plans & Training',
    icon: <HelpOutline sx={{ color: '#6366F1' }} />,
    trigger: 'Sent to member when coach/trainer replies to their query',
    channels: ['Push'],
    placeholders: ['{{subject}}'],
  },

  // ── Retention & Re-engagement ──
  membership_renewal_7_days: {
    type: 'gym',
    title: 'Renewal Notice (7 Days Before)',
    category: 'Retention & Nudges',
    icon: <Schedule sx={{ color: '#F59E0B' }} />,
    trigger: 'Sent 7 days prior to membership expiration',
    channels: ['Push'],
    placeholders: ['{{memberName}}', '{{gymName}}', '{{daysUntilExpiry}}'],
  },
  membership_renewal_day_of_expiry: {
    type: 'gym',
    title: 'Membership Expires Today',
    category: 'Retention & Nudges',
    icon: <WarningAmber sx={{ color: '#EA580C' }} />,
    trigger: 'Sent on the exact day membership reaches its expiration date',
    channels: ['Push'],
    placeholders: ['{{memberName}}', '{{gymName}}'],
  },
  membership_renewal_win_back_3_days: {
    type: 'gym',
    title: 'Win-Back Notice (3 Days After Expiry)',
    category: 'Retention & Nudges',
    icon: <EventRepeat sx={{ color: '#9333EA' }} />,
    trigger: 'Sent 3 days after membership lapse with a renewal prompt',
    channels: ['Push'],
    placeholders: ['{{memberName}}', '{{gymName}}', '{{daysSinceExpiry}}'],
  },
  retention_nudge_high: {
    type: 'gym',
    title: 'Retention Nudge (High Priority)',
    category: 'Retention & Nudges',
    icon: <WarningAmber sx={{ color: '#DC2626' }} />,
    trigger: 'Triggered when member is flagged at high risk of churning',
    channels: ['Push'],
    placeholders: ['{{daysUntilExpiry}}'],
  },
  retention_nudge_medium: {
    type: 'gym',
    title: 'Retention Nudge (Medium Priority)',
    category: 'Retention & Nudges',
    icon: <Schedule sx={{ color: '#F97316' }} />,
    trigger: 'Triggered when member has missed gym check-ins for multiple days',
    channels: ['Push'],
    placeholders: ['{{daysSinceLastCheckin}}'],
  },
  retention_nudge_low: {
    type: 'gym',
    title: 'Retention Nudge (Gentle Check-in)',
    category: 'Retention & Nudges',
    icon: <Schedule sx={{ color: '#3B82F6' }} />,
    trigger: 'Triggered as an encouraging check-in to boost gym attendance',
    channels: ['Push'],
    placeholders: ['{{memberName}}'],
  },
  account_deactivated: {
    type: 'gym',
    title: 'Account Deactivated',
    category: 'Retention & Nudges',
    icon: <PersonOff sx={{ color: '#64748B' }} />,
    trigger: 'Sent when member profile or access is deactivated by admin',
    channels: ['Push'],
    placeholders: [],
  },
};

const CATEGORIES = [
  'All Actions',
  'Membership & Lifecycle',
  'Billing & Invoices',
  'Plans & Training',
  'Retention & Nudges',
];

export default function NotificationTemplates() {
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  })();
  const [gymId, setGymId] = useState(currentUser?.gymId?._id || currentUser?.gymId);

  const [marketingTemplates, setMarketingTemplates] = useState({});
  const [gymNotifs, setGymNotifs] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Actions');

  // Dialog state for editing a single unified copy
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState('');
  const [editType, setEditType] = useState('marketing'); // 'marketing' | 'gym'
  const [editActionTitle, setEditActionTitle] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editRule, setEditRule] = useState({});
  const [editPlaceholders, setEditPlaceholders] = useState([]);
  const [editChannels, setEditChannels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await marketingAPI.getTemplates();
      const catalog = response.data.data;
      const templates = catalog.templates || {};
      setMarketingTemplates(templates);
      setGymId(catalog.gymId || gymId);
      setGymNotifs(Object.fromEntries(Object.entries(templates)
        .filter(([key]) => ACTION_DEFINITIONS[key]?.type !== 'marketing')
        .map(([key, channels]) => [key, { key, ...channels.push }])));
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load notification bodies. Please refresh to try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog for an action's single copy
  const handleEditClick = (key) => {
    const meta = ACTION_DEFINITIONS[key] || {
      type: 'gym',
      title: key.replace(/_/g, ' '),
      category: 'Other',
      channels: ['Push'],
      placeholders: [],
    };

    setEditKey(key);
    setEditType(meta.type);
    setEditActionTitle(meta.title);
    const copy = marketingTemplates[key]?.push || gymNotifs[key] || {};
    const bodyPlaceholders = `${copy.title || ''} ${copy.body || ''}`.match(/\{\{\s*\w+\s*\}\}/g) || [];
    setEditPlaceholders([...new Set([...(meta.placeholders || []), ...bodyPlaceholders])]);
    setEditChannels(['Push', 'SMS', 'WhatsApp']);
    setError('');

    if (meta.type === 'marketing') {
      const mktItem = marketingTemplates[key] || {};
      // Single unified copy derived from available channel text
      const body =
        mktItem.whatsapp?.body ||
        mktItem.push?.body ||
        mktItem.sms?.body ||
        '';
      const isActive = mktItem.whatsapp?.isActive ?? mktItem.push?.isActive ?? true;
      const rule = mktItem.whatsapp?.rule || mktItem.push?.rule || { daysBeforeExpiry: 3 };

      setEditTitle(mktItem.whatsapp?.title || mktItem.push?.title || meta.title);
      setEditBody(body);
      setEditIsActive(isActive);
      setEditRule(rule);
    } else {
      const gymItem = gymNotifs[key] || {};
      setEditTitle(gymItem.title || '');
      setEditBody(gymItem.body || '');
      setEditIsActive(true);
      setEditRule({});
    }

    setEditOpen(true);
  };

  // Save the single copy across all applicable channels
  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editBody.trim()) {
      setError('Notification title and body are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editType === 'gym') {
        const payload = {
          ...gymNotifs,
          [editKey]: {
            ...gymNotifs[editKey],
            key: editKey,
            title: editTitle.trim(),
            body: editBody.trim(),
          }
        };
        await gymsAPI.saveNotificationTemplates(gymId, payload);
      } else {
        // Save the single copy across ALL communication channels (Push, SMS, WhatsApp)
        await marketingAPI.updateTemplate(editKey, 'all', {
          title: editTitle.trim(),
          body: editBody.trim(),
          isActive: editIsActive,
          rule: editRule,
        });
      }

      setEditOpen(false);
      setSuccessMsg(`"${editActionTitle}" template updated successfully for all channels.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset template to default
  const handleReset = async (key) => {
    const meta = ACTION_DEFINITIONS[key];
    const name = meta?.title || key.replace(/_/g, ' ');
    if (!window.confirm(`Reset "${name}" template to default copy for all channels?`)) return;

    try {
      if (meta?.type === 'marketing') {
        await marketingAPI.resetTemplate(key, 'all');
      } else {
        // Clear custom gym override
        const payload = { ...gymNotifs };
        delete payload[key];
        await gymsAPI.saveNotificationTemplates(gymId, payload);
      }
      setSuccessMsg(`"${name}" has been reset to system defaults.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      loadTemplates();
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  // Append a placeholder tag into the body
  const handleInsertPlaceholder = (placeholder) => {
    setEditBody((prev) => (prev ? `${prev} ${placeholder}` : placeholder));
  };

  // Collect and normalize all actions into a unified list
  const combinedActions = useMemo(() => {
    const actionKeys = new Set([
      ...Object.keys(ACTION_DEFINITIONS),
      ...Object.keys(marketingTemplates),
      ...Object.keys(gymNotifs),
    ]);

    return Array.from(actionKeys).map((key) => {
      const meta = ACTION_DEFINITIONS[key] || {
        type: gymNotifs[key] ? 'gym' : 'marketing',
        title: key.replace(/_/g, ' '),
        category: 'Retention & Nudges',
        channels: ['Push'],
        placeholders: [],
        trigger: 'System action notification',
      };

      let body = '';
      let title = '';
      let isCustom = false;
      let isActive = true;

      if (meta.type === 'marketing') {
        const mkt = marketingTemplates[key] || {};
        title = mkt.whatsapp?.title || mkt.push?.title || '';
        body =
          mkt.whatsapp?.body ||
          mkt.push?.body ||
          mkt.sms?.body ||
          '';
        isActive = mkt.whatsapp?.isActive ?? mkt.push?.isActive ?? true;
        isCustom = Boolean(
          mkt.whatsapp?.isCustom ||
          mkt.push?.isCustom ||
          mkt.sms?.isCustom
        );
      } else {
        const item = gymNotifs[key] || {};
        title = item.title || '';
        body = item.body || '';
        isCustom = Boolean(item.updatedAt);
      }

      return {
        key,
        ...meta,
        channels: ['Push', 'SMS', 'WhatsApp'],
        title: title || meta.title,
        actionName: meta.title,
        body,
        isCustom,
        isActive,
      };
    });
  }, [marketingTemplates, gymNotifs]);

  // Filter actions by category and search term
  const filteredActions = useMemo(() => {
    return combinedActions.filter((item) => {
      const matchesCategory =
        selectedCategory === 'All Actions' || item.category === selectedCategory;

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.actionName.toLowerCase().includes(q) ||
        item.key.toLowerCase().includes(q) ||
        item.body.toLowerCase().includes(q) ||
        item.title.toLowerCase().includes(q) ||
        (item.trigger && item.trigger.toLowerCase().includes(q));

      return matchesCategory && matchesSearch;
    });
  }, [combinedActions, selectedCategory, searchQuery]);

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Notification Templates"
        subtitle="Manage a single, unified copy of notifications for each action used across Push, SMS, and WhatsApp."
        gradient="linear-gradient(90deg, #1D4ED8, #60A5FA)"
      />

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {error && !editOpen && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={loadTemplates}>Retry</Button>}>{error}</Alert>}
      {/* Action Search and Category Filters */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            placeholder="Search notification actions, messages, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                clickable
                color={selectedCategory === cat ? 'primary' : 'default'}
                variant={selectedCategory === cat ? 'filled' : 'outlined'}
                onClick={() => setSelectedCategory(cat)}
                sx={{ fontWeight: 600, fontSize: '0.8rem' }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* Notification Actions List */}
      <Stack spacing={2.5}>
        {loading ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">Loading notification templates...</Typography>
          </Paper>
        ) : filteredActions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
              No matching notification actions found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Try searching with different keywords or switch the category filter.
            </Typography>
          </Paper>
        ) : (
          filteredActions.map((action) => (
            <Card
              key={action.key}
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: '#E2E8F0',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.06)' },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header row: Action title, Category, Channels, Edit/Reset */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        bgcolor: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {action.icon || <NotifIcon sx={{ color: '#475569' }} />}
                    </Box>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                          {action.actionName}
                        </Typography>
                        {!action.isActive && (
                          <Chip size="small" label="Disabled" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {action.isCustom && (
                          <Chip size="small" label="Customized" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                        {action.trigger}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                    {action.isCustom && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Refresh />}
                        onClick={() => handleReset(action.key)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Reset
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleEditClick(action.key)}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                      Edit Template
                    </Button>
                  </Stack>
                </Stack>

                {/* Channel badges row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Used by:
                  </Typography>
                  {action.channels.map((ch) => {
                    let icon = <NotifIcon sx={{ fontSize: 13 }} />;
                    let color = '#2563EB';
                    let bg = '#EFF6FF';
                    if (ch === 'SMS') {
                      icon = <SmsIcon sx={{ fontSize: 13 }} />;
                      color = '#D97706';
                      bg = '#FEF3C7';
                    } else if (ch === 'WhatsApp') {
                      icon = <WhatsAppIcon sx={{ fontSize: 13 }} />;
                      color = '#16A34A';
                      bg = '#DCFCE7';
                    }
                    return (
                      <Chip
                        key={ch}
                        size="small"
                        icon={icon}
                        label={ch === 'Push' ? 'Push Notification' : ch}
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          bgcolor: bg,
                          color: color,
                          '& .MuiChip-icon': { color: `${color} !important` },
                        }}
                      />
                    );
                  })}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    (Single copy shared across all channels)
                  </Typography>
                </Stack>

                {/* Single Copy Preview Card */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#F8FAFC',
                    borderColor: '#E2E8F0',
                    borderRadius: 2,
                  }}
                >
                  {action.title && (
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#1E293B"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Title:
                      </Typography>
                      {action.title}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="#334155"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.55,
                      fontFamily: 'inherit',
                    }}
                  >
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Body: </Typography>
                    {action.body || <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>No template copy configured</span>}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>

      {/* Edit Single Copy Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              Edit Template: {editActionTitle}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Changes will apply as the single unified message copy used across all channels (
            {editChannels.join(', ')}).
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Placeholders helper */}
          {editPlaceholders.length > 0 && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 2 }}>
              <Typography variant="caption" fontWeight={700} color="#0369A1" sx={{ display: 'block', mb: 1 }}>
                Available Placeholders (click to insert into message):
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {editPlaceholders.map((ph) => (
                  <Chip
                    key={ph}
                    label={ph}
                    size="small"
                    clickable
                    onClick={() => handleInsertPlaceholder(ph)}
                    sx={{
                      bgcolor: '#E0F2FE',
                      color: '#0284C7',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      '&:hover': { bgcolor: '#BAE6FD' },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Optional Title (for gym actions) */}
          {
            <TextField
              fullWidth
              variant="outlined"
              label="Notification Title"
              required
              inputProps={{ maxLength: 200 }}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              sx={{ mb: 2.5 }}
              helperText="Push headline; included above the body in SMS and WhatsApp"
            />
          }

          {/* Single Unified Message Body */}
          <TextField
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            label="Notification Body"
            required
            inputProps={{ maxLength: 1000 }}
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            helperText="This exact copy will be delivered via Push notification, SMS, and WhatsApp."
          />

          {/* Automation Rule Settings (if applicable) */}
          {editType === 'marketing' && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #E2E8F0' }}>
              <Typography variant="subtitle2" fontWeight={700} color="#1E293B" gutterBottom>
                Automation Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable automated delivery for this action"
              />
              {editKey === 'renewal_reminder' && editIsActive && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Days before expiry"
                    type="number"
                    size="small"
                    value={editRule.daysBeforeExpiry ?? 3}
                    onChange={(e) =>
                      setEditRule({
                        ...editRule,
                        daysBeforeExpiry: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    helperText="Automatically triggers X days before member's plan expires"
                    inputProps={{ min: 0, max: 30 }}
                    sx={{ width: 220 }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            {saving ? 'Saving...' : 'Save for All Channels'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
